import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useState } from "react";
import axios from "@/utils/useAxios";
import BestafLogo from "@/pages/dashboard/misc/BestafLogo";


const formSchema = z
  .object({
    otp: z.string().min(1, "OTP is required"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export type ResetPasswordDTO = z.infer<typeof formSchema>;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isPending, setIsPending] = useState(false);

  // Extract email and type from URL parameters
  const email = searchParams.get("email");

  const form = useForm<ResetPasswordDTO>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      otp: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: ResetPasswordDTO) => {
    if (!email) {
      toast.error("Email not found in URL");
      return;
    }

    setIsPending(true);
    try {
      const payload = {
        email: email.toLowerCase(),
        otp: data.otp,
        password: data.password,
        confirmPassword: data.confirmPassword,
      };

      await axios.put("/clientmerchants/reset-password", payload);

      toast.success("Password reset successfully");
      navigate("/login");
    } catch (error: any) {
      console.error("Reset password error:", error);
      toast.error(error?.response?.data?.message || "Failed to reset password");
    } finally {
      setIsPending(false);
    }
  };

  // If no email in URL, show error
  if (!email) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div>
          <BestafLogo />
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col justify-center items-center">
                <h1 className="text-2xl text-red-600 mb-4">Invalid Reset Link</h1>
                <p className="text-gray-600 mb-4">
                  The reset link is invalid or missing email parameter.
                </p>
                <Button
                  onClick={() => navigate("/forgot-password")}
                  className="bg-primary-500 w-full h-12 py-2"
                >
                  Go to Forgot Password
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center h-screen">
      <div>
        <BestafLogo />
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col justify-center items-center">
              <h1 className="text-3xl mb-4">Reset Password</h1>
              <p className="text-gray-600 mb-4 text-center">
                Reset password for: <span className="font-semibold">{email}</span>
              </p>
            </div>
            <div className="!text-[#333333] w-[500px]">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4 mt-4"
                >
                  <FormField
                    control={form.control}
                    name="otp"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>OTP</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter OTP"
                            className="h-12"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>New Password</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="password"
                            placeholder="Enter New Password"
                            className="h-12"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm Password</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="password"
                            placeholder="Confirm New Password"
                            className="h-12"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    disabled={isPending}
                    isLoading={isPending}
                    type="submit"
                    className="bg-primary-500 w-full h-12 py-2"
                  >
                    Reset Password
                  </Button>
                </form>
              </Form>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
